import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { salaryAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatCurrency } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Receipt, Download, Eye, Loader2, Play, Send } from "lucide-react";

const SalarySlipsPage = () => {
  const { isHRAdmin } = useAuth();
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [viewSlip, setViewSlip] = useState(null);

  useEffect(() => {
    fetchSlips();
  }, []);

  const fetchSlips = async () => {
    try {
      const response = await salaryAPI.getSlips();
      setSlips(response.data);
    } catch (error) {
      toast.error("Failed to fetch salary slips");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedMonth) {
      toast.error("Please select a month");
      return;
    }
    setGenerateLoading(true);
    try {
      const response = await salaryAPI.generateSlips(selectedMonth);
      toast.success(response.data.message);
      setShowGenerateDialog(false);
      setSelectedMonth("");
      fetchSlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate slips");
    } finally {
      setGenerateLoading(false);
    }
  };

  const handlePublish = async (month) => {
    setPublishLoading(true);
    try {
      const response = await salaryAPI.publishSlip(month);
      toast.success(response.data.message);
      fetchSlips();
    } catch (error) {
      toast.error("Failed to publish slips");
    } finally {
      setPublishLoading(false);
    }
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split("-");
    const date = new Date(year, month - 1);
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="salary-slips-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Salary Slips</h1>
          <p className="text-slate-500">View and manage salary slips</p>
        </div>
        {isHRAdmin && (
          <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="generate-slips-btn">
                <Play className="w-4 h-4 mr-2" />
                Generate Slips
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">Generate Salary Slips</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="form-group">
                  <Label>Month</Label>
                  <Input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    data-testid="generate-month-input"
                  />
                </div>
                <Button
                  onClick={handleGenerate}
                  className="w-full btn-primary"
                  disabled={generateLoading}
                  data-testid="submit-generate"
                >
                  {generateLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Slips Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead>Basic</TableHead>
                <TableHead>Gross</TableHead>
                <TableHead>Deductions</TableHead>
                <TableHead>Net Salary</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {slips.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No salary slips found
                  </TableCell>
                </TableRow>
              ) : (
                slips.map((slip) => (
                  <TableRow key={slip.id} data-testid={`slip-row-${slip.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <Receipt className="w-5 h-5 text-emerald-600" />
                        </div>
                        <span className="font-medium">{getMonthName(slip.month)}</span>
                      </div>
                    </TableCell>
                    <TableCell>{formatCurrency(slip.basic_salary)}</TableCell>
                    <TableCell>{formatCurrency(slip.gross_salary)}</TableCell>
                    <TableCell className="text-red-600">-{formatCurrency(slip.total_deductions)}</TableCell>
                    <TableCell className="font-semibold text-emerald-600">
                      {formatCurrency(slip.net_salary)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={slip.is_published ? "default" : "secondary"}>
                        {slip.is_published ? "Published" : "Draft"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setViewSlip(slip)}
                          data-testid={`view-slip-${slip.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon">
                          <Download className="w-4 h-4" />
                        </Button>
                        {isHRAdmin && !slip.is_published && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handlePublish(slip.month)}
                            disabled={publishLoading}
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* View Slip Dialog */}
      <Dialog open={!!viewSlip} onOpenChange={() => setViewSlip(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Salary Slip - {viewSlip && getMonthName(viewSlip.month)}
            </DialogTitle>
          </DialogHeader>
          {viewSlip && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Basic Salary</p>
                  <p className="text-lg font-semibold">{formatCurrency(viewSlip.basic_salary)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Gross Salary</p>
                  <p className="text-lg font-semibold">{formatCurrency(viewSlip.gross_salary)}</p>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium">Components</h4>
                {viewSlip.components?.map((comp, index) => (
                  <div key={index} className="flex justify-between p-2 border-b">
                    <span className={comp.type === "deduction" ? "text-red-600" : ""}>
                      {comp.name}
                    </span>
                    <span className={comp.type === "deduction" ? "text-red-600" : ""}>
                      {comp.type === "deduction" ? "-" : "+"}{formatCurrency(comp.amount)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-4 p-4 bg-emerald-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Total Deductions</p>
                  <p className="text-lg font-semibold text-red-600">
                    -{formatCurrency(viewSlip.total_deductions)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Net Salary</p>
                  <p className="text-lg font-semibold text-emerald-600">
                    {formatCurrency(viewSlip.net_salary)}
                  </p>
                </div>
              </div>

              <Button variant="outline" className="w-full">
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalarySlipsPage;
